import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  HandHelping,
  History,
  Package,
  Pencil,
  RotateCw,
  Boxes,
  Store,
  Undo2,
  Users,
} from 'lucide-react';
import { useIsSuperAdmin, useProfile } from '@/shared/auth/SessionProvider';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import {
  getInventarioDetalle,
  type BacklogItem,
  type InventarioRow,
  type PrestamoItem,
  type ShowroomItem,
} from '../api';
import { DevolucionPrestamoDialog } from './DevolucionPrestamoDialog';
import { EditarInventarioDialog } from './EditarInventarioDialog';
import { EnviarShowroomDialog } from './EnviarShowroomDialog';
import { RegistrarPrestamoDialog } from './RegistrarPrestamoDialog';
import { RetornarShowroomDialog } from './RetornarShowroomDialog';
import { StockPorOrigenTab } from './StockPorOrigen';
import { getUnidadesArticulo } from '../stockUnidades';
import { nombreBucket, textoConBuckets } from '../lib/bucketLabels';
import { SwapDialog } from './SwapDialog';

/**
 * Read-only detail view for an Inventario row. Shows the 7-bucket
 * breakdown (stock global) plus tabs: Stock detallado (cada unidad / ingreso
 * con su importación, llegada y serie; la pestaña por defecto cuando el
 * artículo mueve stock por unidades), Historial (25.BacklogAccionesArticulos),
 * active Préstamos and active Showrooms.
 *
 * I2 adds Editar + Swap actions in the footer. I3/I4 will add Enviar
 * Showroom and Registrar Préstamo alongside.
 */
export function DetalleInventarioDialog({
  summary,
  onClose,
}: {
  summary: InventarioRow;
  onClose: () => void;
}) {
  const detalleQ = useQuery({
    queryKey: ['inventario', 'detalle', summary.id],
    queryFn: () => getInventarioDetalle(summary.id),
    staleTime: 30_000,
  });
  // El historial viene entero en la respuesta; se revela de a 20 para no
  // volcar cientos de filas en el diálogo (y sin viajes extra por click).
  const [historialVisible, setHistorialVisible] = useState(20);

  const detalle = detalleQ.data;
  // null = la pestaña por defecto (se decide abajo, cuando se sabe si el artículo tiene unidades).
  const [tabElegida, setTab] = useState<'prestamos' | 'showrooms' | 'historial' | 'origen' | null>(null);
  const [action, setAction] = useState<
    | 'editar'
    | 'swap'
    | 'enviarShowroom'
    | 'retornarShowroom'
    | 'registrarPrestamo'
    | 'devolucionPrestamo'
    | null
  >(null);

  const row = detalle?.row ?? summary;
  const totalMismatch = detalle ? Math.abs(row.totalStored - row.totalStock) > 0.01 : false;
  const canSwap = row.buckets.porIngresar > 0;
  const canEnviarShowroom = row.buckets.disponible > 0;
  const canRetornarShowroom = row.buckets.prestShow > 0;
  const canRegistrarPrestamo = row.buckets.disponible > 0;
  const showrooms = detalle?.showrooms ?? [];
  const prestamos = detalle?.prestamos ?? [];
  const canDevolverPrestamo = row.buckets.prestShow > 0 && prestamos.length > 0;
  // Inventario writes are admin-only. Other roles (administracion,
  // ventas, mantenimiento) see this dialog read-only — no edit /
  // swap / showroom / préstamo footer at all.
  const canEdit = useIsSuperAdmin();
  // El stock por origen (compra / importación de cada unidad) lo leen admin,
  // administración y mantenimiento; ventas y marketing no tienen acceso a la API.
  const perfil = useProfile()?.perfil;
  const puedeVerOrigen = perfil === 'admin' || perfil === 'administracion' || perfil === 'mantenimiento';
  // Misma query (y caché) que la pestaña: decide si se muestra, si abre por defecto y su contador.
  const unidadesQ = useQuery({
    queryKey: ['inventario', 'unidades', summary.id],
    queryFn: () => getUnidadesArticulo(summary.id),
    staleTime: 30_000,
    enabled: puedeVerOrigen,
  });
  // Sin unidades (servicios, Ferretería, migración sin aplicar) la pestaña no aporta: se oculta.
  const sinUnidades =
    unidadesQ.data != null && (!unidadesQ.data.habilitado || !unidadesQ.data.articulo?.impactaStock);
  const veOrigen = puedeVerOrigen && !unidadesQ.isError && !sinUnidades;
  const enStock = unidadesQ.data?.unidades.filter((u) => u.estado !== 'agotada').length ?? null;
  // Por defecto, Stock detallado; mientras carga se muestra su esqueleto (no salta desde Historial).
  const tab = tabElegida ?? (veOrigen ? 'origen' : 'historial');

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{row.fields.Articulo_I || 'Artículo sin nombre'}</DialogTitle>
          <DialogDescription>
            {row.fields.TipoInventario_I || '—'}
            {row.fields.FechaActualizacion_I && (
              <>
                {' · '}
                <span className="text-xs">
                  Última actualización: {row.fields.FechaActualizacion_I}
                  {row.fields.HoraActualizacion_I ? ` ${row.fields.HoraActualizacion_I}` : ''}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <BucketStrip row={row} />
          {totalMismatch && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                El TotalStock almacenado ({row.totalStored}) no coincide con la suma de los
                buckets ({row.totalStock}). Una escritura previa no recomputó el total —
                puede corregirse desde el próximo ajuste manual.
              </span>
            </div>
          )}

          <div className="flex items-center gap-1 border-b">
            {veOrigen && (
              <TabButton
                active={tab === 'origen'}
                onClick={() => setTab('origen')}
                icon={<Boxes className="h-4 w-4" aria-hidden />}
                label="Stock detallado"
                mobileLabel="Detalle"
                count={enStock}
              />
            )}
            <TabButton
              active={tab === 'historial'}
              onClick={() => setTab('historial')}
              icon={<History className="h-4 w-4" aria-hidden />}
              label="Historial"
              mobileLabel="Hist."
              count={detalle?.backlog.length ?? null}
            />
            <TabButton
              active={tab === 'prestamos'}
              onClick={() => setTab('prestamos')}
              icon={<Users className="h-4 w-4" aria-hidden />}
              label="Préstamos activos"
              mobileLabel="Préstamos"
              count={detalle?.prestamos.length ?? null}
            />
            <TabButton
              active={tab === 'showrooms'}
              onClick={() => setTab('showrooms')}
              icon={<Package className="h-4 w-4" aria-hidden />}
              label="Showroom activo"
              mobileLabel="Showroom"
              count={detalle?.showrooms.length ?? null}
            />
          </div>

          {tab === 'origen' && veOrigen ? (
            <StockPorOrigenTab inventarioId={row.id} />
          ) : detalleQ.isPending ? (
            <LoadingPanel />
          ) : detalleQ.isError ? (
            <div className="text-sm text-destructive">
              No se pudo cargar el detalle.
            </div>
          ) : !detalle ? null : tab === 'historial' ? (
            <HistorialTab
              rows={detalle.backlog.slice(0, historialVisible)}
              restantes={Math.max(0, detalle.backlog.length - historialVisible)}
              recortados={Math.max(0, detalle.backlogTotal - detalle.backlog.length)}
              onMore={() => setHistorialVisible((n) => n + 50)}
            />
          ) : tab === 'prestamos' ? (
            <PrestamosTab rows={detalle.prestamos} backlog={detalle.backlog} />
          ) : (
            <ShowroomsTab rows={detalle.showrooms} backlog={detalle.backlog} />
          )}
        </DialogBody>
        {/* `flex-row` overrides DialogFooter's default `flex-col-reverse`
            on mobile so the action row + Editar stay on a single wrapping
            row. Footer is wrapped in `canEdit &&` below — non-admin roles
            see this dialog read-only with no action buttons. */}
        {canEdit && (
        <DialogFooter className="flex-row flex-wrap items-center justify-end gap-1.5 sm:justify-start sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAction('swap')}
            disabled={!canSwap}
            title={
              canSwap
                ? 'Corrección manual: mover unidades de Por Ingresar a Disponible (lo normal es recibirlas desde Recepción)'
                : 'Sin stock Por Ingresar para mover'
            }
            aria-label="Corrección Por ingresar a Disponible"
            className="h-9 w-9 gap-0 px-0 sm:w-auto sm:gap-2 sm:px-3"
          >
            <RotateCw className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Corrección: Por ingresar → Disponible</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAction('enviarShowroom')}
            disabled={!canEnviarShowroom}
            title={
              canEnviarShowroom
                ? 'Enviar unidades al showroom'
                : 'Sin stock Disponible para enviar'
            }
            aria-label="Enviar al Showroom"
            className="h-9 w-9 gap-0 px-0 sm:w-auto sm:gap-2 sm:px-3"
          >
            <Store className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Env. Showroom</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAction('retornarShowroom')}
            disabled={!canRetornarShowroom}
            title={
              canRetornarShowroom
                ? 'Retornar unidades desde el showroom'
                : 'Sin stock en showroom para retornar'
            }
            aria-label="Retornar del Showroom"
            className="h-9 w-9 gap-0 px-0 sm:w-auto sm:gap-2 sm:px-3"
          >
            <Undo2 className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Ret. Showroom</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAction('registrarPrestamo')}
            disabled={!canRegistrarPrestamo}
            title={
              canRegistrarPrestamo
                ? 'Registrar un préstamo a un cliente'
                : 'Sin stock Disponible para prestar'
            }
            aria-label="Registrar Préstamo"
            className="h-9 w-9 gap-0 px-0 sm:w-auto sm:gap-2 sm:px-3"
          >
            <HandHelping className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Reg. Préstamo</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAction('devolucionPrestamo')}
            disabled={!canDevolverPrestamo}
            title={
              canDevolverPrestamo
                ? 'Registrar la devolución de un préstamo'
                : prestamos.length === 0
                  ? 'Sin préstamos activos'
                  : 'Sin stock en Prest/Show'
            }
            aria-label="Devolver Préstamo"
            className="h-9 w-9 gap-0 px-0 sm:w-auto sm:gap-2 sm:px-3"
          >
            <Undo2 className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Dev. Préstamo</span>
          </Button>
          <Button
            onClick={() => setAction('editar')}
            size="sm"
            // El formulario necesita `holds` (lo comprometido por ventas) para
            // avisar en vivo, y eso viene con el detalle.
            disabled={!detalle}
            className="gap-2 sm:ml-auto"
            title={detalle ? 'Editar' : 'Cargando…'}
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" aria-hidden />
            Editar
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
      {action === 'editar' && detalle && (
        <EditarInventarioDialog
          row={row}
          holds={detalle.holds}
          onClose={() => setAction(null)}
        />
      )}
      {action === 'swap' && (
        <SwapDialog row={row} onClose={() => setAction(null)} />
      )}
      {action === 'enviarShowroom' && (
        <EnviarShowroomDialog row={row} onClose={() => setAction(null)} />
      )}
      {action === 'retornarShowroom' && (
        <RetornarShowroomDialog
          row={row}
          activePlacements={showrooms}
          onClose={() => setAction(null)}
        />
      )}
      {action === 'registrarPrestamo' && (
        <RegistrarPrestamoDialog row={row} onClose={() => setAction(null)} />
      )}
      {action === 'devolucionPrestamo' && (
        <DevolucionPrestamoDialog
          row={row}
          activePrestamos={prestamos}
          onClose={() => setAction(null)}
        />
      )}
    </Dialog>
  );
}

function BucketStrip({ row }: { row: InventarioRow }) {
  const items: Array<{
    label: string;
    mobileLabel: string;
    value: number;
    tone: 'green' | 'sky' | 'amber' | 'violet' | 'rose' | 'slate';
  }> = [
    { label: 'Disponible', mobileLabel: 'Disp.', value: row.buckets.disponible, tone: 'green' },
    { label: 'Pago Pendiente', mobileLabel: 'P. Pend.', value: row.buckets.reservado, tone: 'amber' },
    { label: 'Pago Parcial', mobileLabel: 'P. Parc.', value: row.buckets.reservadoParcial, tone: 'amber' },
    { label: 'Pendiente Despacho', mobileLabel: 'P. Desp.', value: row.buckets.pendienteDespacho, tone: 'violet' },
    { label: 'Por Ingresar', mobileLabel: 'P. Ing.', value: row.buckets.porIngresar, tone: 'sky' },
    { label: 'Prest/Show', mobileLabel: 'Prest.', value: row.buckets.prestShow, tone: 'rose' },
  ];

  const toneClass: Record<(typeof items)[number]['tone'], string> = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    sky: 'border-sky-200 bg-sky-50 text-sky-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  };

  // A negative bucket should never happen — but if a write went wrong
  // we want it to be loud. Override the tile to a rose tone (with a
  // ring) so admins can spot which bucket drifted below zero.
  const negativeClass = 'border-rose-300 bg-rose-50 text-rose-700 ring-1 ring-rose-300';

  return (
    <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-2 sm:gap-2 md:grid-cols-4">
      {items.map((b) => {
        const isNegative = b.value < 0;
        return (
          <div
            key={b.label}
            className={cn(
              'flex flex-col rounded-lg border px-2 py-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:px-3',
              isNegative ? negativeClass : toneClass[b.tone],
            )}
          >
            <span className="text-[10px] uppercase tracking-wide opacity-80 sm:text-[11px]">
              <span className="sm:hidden">{b.mobileLabel}</span>
              <span className="hidden sm:inline">{b.label}</span>
            </span>
            <span className="text-base font-semibold tabular-nums sm:text-lg">
              {b.value}
            </span>
          </div>
        );
      })}
      <div
        className={cn(
          'flex flex-col rounded-lg border px-2 py-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:px-3',
          row.totalStock < 0
            ? negativeClass
            : 'border-foreground/30 bg-muted/40',
        )}
      >
        <span
          className={cn(
            'text-[10px] uppercase tracking-wide sm:text-[11px]',
            row.totalStock < 0 ? 'opacity-80' : 'text-muted-foreground',
          )}
        >
          <span className="sm:hidden">Total</span>
          <span className="hidden sm:inline">Total Stock</span>
        </span>
        <span className="text-base font-semibold tabular-nums sm:text-lg">
          {row.totalStock}
        </span>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  mobileLabel,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  mobileLabel?: string;
  count: number | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 border-b-2 px-2 py-2 text-xs transition-colors sm:gap-2 sm:px-3 sm:text-sm',
        active
          ? 'border-foreground text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {icon}
      <span>
        {mobileLabel ? (
          <>
            <span className="sm:hidden">{mobileLabel}</span>
            <span className="hidden sm:inline">{label}</span>
          </>
        ) : (
          label
        )}
      </span>
      {count !== null && (
        <span className="inline-flex min-w-5 justify-center rounded-full bg-muted px-1.5 text-[11px] font-semibold text-muted-foreground">
          {count}
        </span>
      )}
    </button>
  );
}

function LoadingPanel() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/**
 * Render a BAA row's Fecha + Hora in the user's local browser timezone.
 *
 * Storage stays as the row originally landed (Vercel writes UTC, legacy
 * dev writes Argentina local — see api.ts BacklogItem comment). For
 * display, prefer the canonical UTC `createdDateTime` ISO and let the
 * browser format it; fall back to the pre-formatted Fecha_BAA + Hora_BAA
 * strings when the envelope lacks the ISO (defensive).
 */
function BaaFechaCell({ row }: { row: BacklogItem }) {
  const iso = row.createdDateTime ?? row.lastModifiedDateTime;
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const date = d.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const time = d.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return (
        <>
          <div>{date}</div>
          <div className="text-[11px] opacity-70">{time}</div>
        </>
      );
    }
  }
  // Fallback: show the raw stored strings as-is (no TZ conversion possible).
  return (
    <>
      <div>{row.fields.Fecha_BAA || '—'}</div>
      {row.fields.Hora_BAA && (
        <div className="text-[11px] opacity-70">{row.fields.Hora_BAA}</div>
      )}
    </>
  );
}

/**
 * Read the user who triggered a BAA row. Returns empty string when
 * UsuarioApp_BAA is missing — the table renders a dash in that case.
 *
 * (Earlier versions fell back to AppVer_BAA on the theory that very
 * old rows had stored the username there, but in practice that
 * column holds build strings like `20260424_2.23.107` which leak
 * into the UI as confusing version stamps. Rather than try to detect
 * username-vs-version heuristically, drop the fallback entirely.)
 */
function baaUser(r: BacklogItem): string {
  return (r.fields.UsuarioApp_BAA && r.fields.UsuarioApp_BAA.trim()) || '';
}

/**
 * Find the BAA row that originally created a Prestamo / Showroom placement,
 * matched by `IDInventarioDetalle_BAA = placement.id` and the action codigo.
 * Used to attribute "registrado por X" on the active-placements tabs.
 */
function findRegistrationBaa(
  backlog: BacklogItem[],
  codigo: 'PP' | 'SR',
  placementId: string,
): BacklogItem | undefined {
  return backlog.find(
    (b) =>
      b.fields.CodigoStatus_BAA === codigo &&
      (b.fields.IDInventarioDetalle_BAA ?? '') === placementId,
  );
}

function HistorialTab({
  rows,
  restantes,
  recortados,
  onMore,
}: {
  rows: BacklogItem[];
  /** Filas ya traídas que todavía no se muestran. */
  restantes: number;
  /** Filas que el tope del server dejó afuera de la respuesta. */
  recortados: number;
  onMore: () => void;
}) {
  if (!rows || rows.length === 0) {
    return <EmptyRow>Sin entradas en el historial para este artículo.</EmptyRow>;
  }
  return (
    <div className="max-h-[min(20rem,35vh)] overflow-y-auto rounded-lg border bg-card">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-muted text-left text-[11px] uppercase tracking-wide text-muted-foreground shadow-[0_1px_0_0_hsl(var(--border))]">
          <tr>
            <th className="px-2 py-2">Fecha</th>
            <th className="px-2 py-2">Acción</th>
            <th className="px-2 py-2">Movimiento</th>
            <th className="px-2 py-2 text-right">Cantidad</th>
            <th className="px-2 py-2 text-right">Usuario</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => {
            // El historial guarda códigos (Disp, PorIng…); se muestran con el nombre de la tarjeta.
            const origen = nombreBucket(r.fields.BucketOrigen_BAA);
            const destino = nombreBucket(r.fields.BucketDestino_BAA);
            const cantidad = r.fields.Cantidad_BAA?.trim();
            const usuario = baaUser(r);
            return (
              <tr key={r.id} className="hover:bg-muted/20">
                <td className="px-2 py-2 align-top text-muted-foreground">
                  <BaaFechaCell row={r} />
                </td>
                <td className="px-2 py-2 align-top">
                  <CodigoBadge codigo={r.fields.CodigoStatus_BAA ?? ''} />
                </td>
                <td className="px-2 py-2 align-top">
                  <div className="font-medium">{r.fields.Status_BAA || '—'}</div>
                  {(origen || destino) && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {origen ?? '—'} <span aria-hidden>→</span> {destino ?? '—'}
                    </div>
                  )}
                  {r.fields.Descripcion_BAA && (
                    <div
                      className="mt-0.5 text-xs text-muted-foreground line-clamp-2"
                      title={textoConBuckets(r.fields.Descripcion_BAA)}
                    >
                      {textoConBuckets(r.fields.Descripcion_BAA)}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 align-top text-right tabular-nums">
                  {cantidad || <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-2 py-2 align-top text-right text-muted-foreground">
                  {usuario || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(restantes > 0 || recortados > 0) && (
        <div className="sticky bottom-0 border-t bg-card p-2 text-center">
          {restantes > 0 ? (
            <button
              type="button"
              onClick={onMore}
              className="text-xs font-medium text-primary hover:underline"
            >
              Ver más movimientos ({restantes})
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">
              Se muestran los {rows.length} movimientos más recientes; hay{' '}
              {recortados} más antiguos.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function PrestamosTab({ rows, backlog }: { rows: PrestamoItem[]; backlog: BacklogItem[] }) {
  if (!rows || rows.length === 0) {
    return <EmptyRow>No hay préstamos activos para este artículo.</EmptyRow>;
  }
  return (
    <div className="max-h-[min(20rem,35vh)] overflow-y-auto rounded-lg border bg-card">
      <ul className="divide-y divide-border">
        {rows.map((r) => {
          const registrationBaa = findRegistrationBaa(backlog, 'PP', r.id);
          const usuario = registrationBaa ? baaUser(registrationBaa) : '';
          return (
            <li
              key={r.id}
              className="flex items-start justify-between px-3 py-2 text-sm"
            >
              <div>
                <div className="font-medium">{r.fields.Cliente_PPRES || '—'}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Desde {r.fields.FechaPrestamo_PPRES || '—'}
                  {r.fields.FechaPrestamoFin_PPRES
                    ? ` · hasta ${r.fields.FechaPrestamoFin_PPRES}`
                    : ''}
                </div>
                {usuario && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Registrado por <span className="font-medium">{usuario}</span>
                  </div>
                )}
              </div>
              <span className="inline-flex rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                {r.fields.Status_PPRES || 'Activo'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ShowroomsTab({ rows, backlog }: { rows: ShowroomItem[]; backlog: BacklogItem[] }) {
  if (!rows || rows.length === 0) {
    return <EmptyRow>No hay stock en showroom para este artículo.</EmptyRow>;
  }
  return (
    <div className="max-h-[min(20rem,35vh)] overflow-y-auto rounded-lg border bg-card">
      <ul className="divide-y divide-border">
        {rows.map((r) => {
          const registrationBaa = findRegistrationBaa(backlog, 'SR', r.id);
          const usuario = registrationBaa ? baaUser(registrationBaa) : '';
          return (
            <li
              key={r.id}
              className="flex items-start justify-between px-3 py-2 text-sm"
            >
              <div>
                <div className="font-medium">Showroom #{r.id}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Desde {r.fields.FechaShowRoom_SR || '—'}
                  {r.fields.FechaFinShowRoom_SR
                    ? ` · hasta ${r.fields.FechaFinShowRoom_SR}`
                    : ''}
                </div>
                {usuario && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Enviado por <span className="font-medium">{usuario}</span>
                  </div>
                )}
              </div>
              <span className="inline-flex rounded-md bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                {r.fields.Status_SR || 'Activo'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Map BAA `CodigoStatus_BAA` to a user-facing label + color. The 2-char code
 * is internal SharePoint jargon (SR / PP / VS / etc.) — admins shouldn't have
 * to memorize it, so the badge renders the full label and falls back to the
 * raw code only when we don't recognize it (defensive — new codes can be
 * added by writer code without breaking the UI).
 */
const CODIGO_DETAIL: Record<string, { label: string; cls: string }> = {
  SR: { label: 'Showroom', cls: 'bg-sky-100 text-sky-700' },
  PP: { label: 'Préstamo', cls: 'bg-rose-100 text-rose-700' },
  EM: { label: 'Edición Manual', cls: 'bg-amber-100 text-amber-700' },
  SW: { label: 'Swap Ingreso', cls: 'bg-violet-100 text-violet-700' },
  RC: { label: 'Recepción', cls: 'bg-teal-100 text-teal-700' },
  RE: { label: 'Restore', cls: 'bg-emerald-100 text-emerald-700' },
  VS: { label: 'Movimiento Venta', cls: 'bg-indigo-100 text-indigo-700' },
  VE: { label: 'Vendido', cls: 'bg-emerald-100 text-emerald-800' },
  DP: { label: 'Despacho', cls: 'bg-slate-100 text-slate-700' },
};

function CodigoBadge({ codigo }: { codigo: string }) {
  if (!codigo) return <span className="text-muted-foreground">—</span>;
  const detail = CODIGO_DETAIL[codigo];
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-semibold',
        detail?.cls ?? 'bg-secondary text-foreground',
      )}
      title={detail ? `Código BAA: ${codigo}` : undefined}
    >
      {detail?.label ?? codigo}
    </span>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}
